import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Home, ClipboardCheck, Brain, Search, Menu, X,
  BookOpen, Activity, LineChart, Bell, BarChart3, 
  Video, Sparkles, User, LogOut, Heart, Camera
} from 'lucide-react';

// Haptic feedback utility
const haptic = (style = 'light') => {
  // iOS Safari haptic (if available)
  if (window.navigator?.vibrate) {
    const patterns = { light: 10, medium: 20, heavy: 30 };
    window.navigator.vibrate(patterns[style] || 10);
  }
  // AudioContext-based subtle click for iOS (which blocks vibrate)
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    gain.gain.value = 0.01;
    osc.frequency.value = 200;
    osc.start();
    osc.stop(ctx.currentTime + 0.01);
  } catch (_) {}
};

const HIDDEN_ROUTES = [
  '/', '/auth', '/forgot-password', '/reset-password', 
  '/verify-email', '/quiz', '/provider'
];

const shouldHide = (pathname) => {
  if (HIDDEN_ROUTES.includes(pathname)) return true;
  if (pathname.startsWith('/admin')) return true;
  if (pathname.startsWith('/shared/')) return true;
  return false;
};

const moreMenuItems = [
  { icon: BookOpen, label: 'Journal', path: '/journal' },
  { icon: Activity, label: 'Symptoms', path: '/symptoms' },
  { icon: LineChart, label: 'Analytics', path: '/analytics' },
  { icon: Bell, label: 'Reminders', path: '/reminders' },
  { icon: BarChart3, label: 'Compare', path: '/compare' },
  { icon: Video, label: 'Telehealth', path: '/telehealth' },
  { icon: Sparkles, label: 'Supplements', path: '/supplements' },
  { icon: User, label: 'Profile', path: '/profile' },
];

export default function BottomNavigation() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [showMore, setShowMore] = useState(false);

  if (!user || shouldHide(location.pathname)) return null;

  const tabs = [
    { icon: Home, label: 'Home', path: '/dashboard' },
    { icon: ClipboardCheck, label: 'Quiz', path: '/quiz' },
    { icon: Brain, label: 'Ask AI', path: '/ask', center: true },
    { icon: Search, label: 'Lookup', path: '/lookup' },
    { icon: Menu, label: 'More', action: 'more' },
  ];

  const isActive = (path) => location.pathname === path;

  const handleTab = (tab) => {
    haptic('light');
    if (tab.action === 'more') {
      setShowMore(true);
    } else {
      navigate(tab.path);
    }
  };

  return (
    <>
      {/* Spacer to prevent content from hiding behind nav */}
      <div className="h-16 md:hidden" />

      {/* Bottom Navigation Bar */}
      <nav 
        className="fixed bottom-0 left-0 right-0 z-40 md:hidden bg-white border-t border-border/60 shadow-[0_-2px_10px_rgba(0,0,0,0.06)]"
        style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
        data-testid="bottom-navigation"
      >
        <div className="flex items-center justify-around h-16 max-w-lg mx-auto px-2">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const active = tab.path && isActive(tab.path);

            if (tab.center) {
              return (
                <button
                  key={tab.label}
                  onClick={() => handleTab(tab)}
                  data-testid={`bottom-nav-${tab.label.toLowerCase().replace(/\s/g, '-')}`}
                  className="flex flex-col items-center justify-center -mt-4"
                >
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center shadow-md transition-all ${
                    active 
                      ? 'bg-primary text-white scale-105' 
                      : 'bg-primary/90 text-white hover:bg-primary'
                  }`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <span className={`text-[10px] mt-0.5 font-medium ${active ? 'text-primary' : 'text-muted-foreground'}`}>
                    {tab.label}
                  </span>
                </button>
              );
            }

            return (
              <button
                key={tab.label}
                onClick={() => handleTab(tab)}
                data-testid={`bottom-nav-${tab.label.toLowerCase().replace(/\s/g, '-')}`}
                className="flex flex-col items-center justify-center gap-0.5 py-1 px-3 transition-colors"
              >
                <Icon className={`w-5 h-5 transition-colors ${
                  active ? 'text-primary' : 'text-muted-foreground'
                }`} />
                <span className={`text-[10px] font-medium transition-colors ${
                  active ? 'text-primary' : 'text-muted-foreground'
                }`}>
                  {tab.label}
                </span>
                {active && (
                  <motion.div 
                    layoutId="bottomNavIndicator"
                    className="w-1 h-1 rounded-full bg-primary"
                  />
                )}
              </button>
            );
          })}
        </div>
      </nav>

      {/* More Menu Overlay */}
      <AnimatePresence>
        {showMore && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/40 z-50 md:hidden"
              onClick={() => setShowMore(false)}
            />
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 30, stiffness: 300 }}
              className="fixed bottom-0 left-0 right-0 z-50 md:hidden bg-white rounded-t-2xl shadow-2xl"
              style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
              data-testid="bottom-nav-more-menu"
            >
              {/* Handle bar */}
              <div className="flex justify-center pt-3 pb-1">
                <div className="w-10 h-1 rounded-full bg-gray-300" />
              </div>

              <div className="flex items-center justify-between px-5 py-3 border-b border-border/50">
                <h3 className="font-semibold text-[#1A1A1A]">Menu</h3>
                <button 
                  onClick={() => setShowMore(false)}
                  className="p-1.5 rounded-full hover:bg-gray-100 transition-colors"
                >
                  <X className="w-5 h-5 text-muted-foreground" />
                </button>
              </div>

              <div className="grid grid-cols-4 gap-1 p-4">
                {moreMenuItems.map((item) => {
                  const Icon = item.icon;
                  const active = isActive(item.path);
                  return (
                    <button
                      key={item.path}
                      onClick={() => {
                        haptic('light');
                        setShowMore(false);
                        navigate(item.path);
                      }}
                      data-testid={`more-menu-${item.label.toLowerCase()}`}
                      className={`flex flex-col items-center gap-1.5 p-3 rounded-xl transition-colors ${
                        active ? 'bg-primary/10' : 'hover:bg-gray-50'
                      }`}
                    >
                      <Icon className={`w-5 h-5 ${active ? 'text-primary' : 'text-muted-foreground'}`} />
                      <span className={`text-xs font-medium ${active ? 'text-primary' : 'text-muted-foreground'}`}>
                        {item.label}
                      </span>
                    </button>
                  );
                })}
              </div>

              <div className="px-4 pb-4">
                <button
                  onClick={() => {
                    haptic('medium');
                    setShowMore(false);
                    logout();
                    navigate('/');
                  }}
                  data-testid="more-menu-signout"
                  className="w-full flex items-center gap-3 p-3 rounded-xl text-red-600 hover:bg-red-50 transition-colors"
                >
                  <LogOut className="w-5 h-5" />
                  <span className="text-sm font-medium">Sign Out</span>
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
